import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { WHATS_ON_PAGE_SIZE, WhatsOnPost, WhatsOnService } from 'src/app/services/whats-on.service';

/** Bodies shorter than this are shown whole, with no "Read more". */
const SHORT_BODY_LENGTH = 160;

/**
 * A restaurant's paid What's On posts as a message trail: the Foodie News pattern
 * (picture, when, headline, copy), laid out across the wide menu column so each
 * post is readable in place instead of behind a popup.
 */
@Component({
  selector: 'app-whats-on-trail',
  standalone: true,
  imports: [NgIf, NgFor],
  templateUrl: './whats-on-trail.component.html',
  styleUrls: ['./whats-on-trail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhatsOnTrailComponent implements OnChanges, OnDestroy {
  @Input() branchId!: number;
  @Input() businessName = '';

  posts: WhatsOnPost[] = [];
  total = 0;
  loading = false;
  error = false;
  readonly skeletonRows = [0, 1, 2];
  private readonly expanded = new Set<number>();
  private readonly failedImages = new Set<number>();
  private request?: Subscription;

  constructor(private whatsOn: WhatsOnService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['branchId']) {
      this.posts = [];
      this.total = 0;
      this.expanded.clear();
      this.failedImages.clear();
      this.load(0);
    }
  }

  get hasMore(): boolean {
    return this.posts.length < this.total;
  }

  load(offset: number): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.error = false;
    this.request = this.whatsOn.posts(this.branchId, offset, WHATS_ON_PAGE_SIZE).subscribe({
      next: page => {
        const known = new Set(this.posts.map(post => post.id));
        this.posts = offset === 0 ? page.posts : [...this.posts, ...page.posts.filter(post => !known.has(post.id))];
        this.total = page.total;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.error = true;
        this.cdr.markForCheck();
      }
    });
  }

  loadMore(): void {
    if (!this.loading) this.load(this.posts.length);
  }

  retry(): void {
    this.load(this.posts.length);
  }

  isCollapsible(post: WhatsOnPost): boolean {
    return post.body.length > 1 || (post.body[0]?.length || 0) > SHORT_BODY_LENGTH;
  }

  isExpanded(post: WhatsOnPost): boolean {
    return this.expanded.has(post.id);
  }

  toggle(post: WhatsOnPost): void {
    if (this.expanded.has(post.id)) this.expanded.delete(post.id);
    else this.expanded.add(post.id);
  }

  showImage(post: WhatsOnPost): boolean {
    return !!post.image && !this.failedImages.has(post.id);
  }

  onImageError(post: WhatsOnPost): void {
    this.failedImages.add(post.id);
    this.cdr.markForCheck();
  }

  trackById(_: number, post: WhatsOnPost): number {
    return post.id;
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }
}
